import type { UsageByAgent } from '@ralph/contracts'
import type { I18n } from '@/i18n/index.ts'

/**
 * The pure half of the per-agent cut (AGM-120, OBS-017).
 *
 * One row per agent that ran in the window, carrying the three derived numbers an administrator
 * cannot get by looking: what share of the spend it took, what one successful run cost, and
 * whether the knowledge attached to it is doing anything.
 *
 * The rule this module exists to enforce is that a ratio is null until it means something. A
 * zero-hit rate computed from two searches is noise that reads as a catastrophe; a cost per
 * successful run computed from zero successful runs is a division by zero wearing a dollar sign.
 * Both come back null, and the screen prints a sentence where the number would have been.
 */

/**
 * Below this many searches, the zero-hit rate is withheld.
 *
 * Deliberately the same threshold the server applies in `domain/engagement/analytics.ts`, so an
 * administrator and an owner looking at the same agent are never shown one rate and one "not
 * enough use yet". If that constant moves, this one moves with it.
 */
export const MIN_SEARCHES_FOR_RATE = 10

export type AgentUsageRow = UsageByAgent & {
  /** Share of the window's total spend, 0…1. Null when nothing was spent at all. */
  costShare: number | null
  /** Length of this row's bar, relative to the biggest spender on screen. */
  barFill: number
  /** Spend divided by runs that completed — the unit cost of a task that worked. */
  costPerSuccess: number | null
  successRate: number | null
  /** Authorized searches that came back empty, over searches. Null below the threshold. */
  zeroHitRate: number | null
  knowledgeDocuments: number
  knowledgeBytes: number
  /** Attached bases that no answer in the window pointed at. */
  unusedBases: number
}

const rate = (part: number, whole: number): number | null => (whole > 0 ? part / whole : null)

/**
 * Rows in spend order, with their derived figures.
 *
 * Spend order rather than run order because this table's job is to answer "where is the money
 * going"; the run count sits in the same row for whoever is asking the other question. The
 * server already returns them sorted, and this re-sorts anyway — a client that depends on the
 * order of a list it did not sort is one endpoint change away from a silently wrong ranking.
 */
export const agentUsageRows = (byAgent: readonly UsageByAgent[]): AgentUsageRow[] => {
  const ordered = [...byAgent].sort((a, b) => b.costUsd - a.costUsd || b.runs - a.runs)
  const total = ordered.reduce((sum, agent) => sum + agent.costUsd, 0)
  const peak = ordered.reduce((max, agent) => Math.max(max, agent.costUsd), 0)

  return ordered.map((agent) => ({
    ...agent,
    costShare: total > 0 ? agent.costUsd / total : null,
    // A run with no recorded spend still happened, so a zero-cost row keeps a visible stub
    // rather than disappearing into the axis. Without a peak there is nothing to be relative to.
    barFill: peak > 0 ? Math.max(0.02, agent.costUsd / peak) : 0,
    costPerSuccess: agent.successfulRuns > 0 ? agent.costUsd / agent.successfulRuns : null,
    successRate: rate(agent.successfulRuns, agent.runs),
    zeroHitRate: agent.searches >= MIN_SEARCHES_FOR_RATE ? agent.zeroHitSearches / agent.searches : null,
    knowledgeDocuments: agent.knowledgeBases.reduce((sum, base) => sum + base.documentCount, 0),
    knowledgeBytes: agent.knowledgeBases.reduce((sum, base) => sum + base.storageBytes, 0),
    unusedBases: agent.knowledgeBases.filter((base) => base.citations === 0).length
  }))
}

export type UsageTotals = {
  agents: number
  runs: number
  costUsd: number
  /** Agents that ran without citing anything — spend with no evidence behind it. */
  ungroundedAgents: number
}

/**
 * The one-line summary above the table.
 *
 * `ungroundedAgents` counts agents that HAVE knowledge attached and cited none of it. An agent
 * with nothing attached is not ungrounded, it is a different kind of agent — counting it here
 * would make every summarizer and drafting assistant on the deployment look like a fault.
 */
export const usageTotals = (byAgent: readonly UsageByAgent[]): UsageTotals => ({
  agents: byAgent.length,
  runs: byAgent.reduce((sum, agent) => sum + agent.runs, 0),
  costUsd: byAgent.reduce((sum, agent) => sum + agent.costUsd, 0),
  ungroundedAgents: byAgent.filter((agent) => agent.knowledgeBases.length > 0 && agent.citations === 0).length
})

/**
 * Runs the window counted that no agent row claims.
 *
 * The two figures come from the same endpoint over the same window and differ by exactly one
 * rule: the totals count every run, and the per-agent cut drops builder previews. So the
 * difference IS the preview count, and stating it is the difference between a screen that adds
 * up and one where "8 runs" sits three centimetres above "7 runs" with no explanation — which
 * is how a reader decides a dashboard cannot be trusted.
 *
 * Floored at zero. It should not be possible for the rows to exceed the total, and if a future
 * endpoint change ever made it so, a negative "excluded" count would be a worse lie than
 * silence.
 */
export const previewRunsExcluded = (windowRuns: number, byAgent: readonly UsageByAgent[]): number =>
  Math.max(0, windowRuns - byAgent.reduce((sum, agent) => sum + agent.runs, 0))

/**
 * Bases ordered as the panel shows them: what answered, then what is merely large.
 *
 * The server sorts the same way. Repeated here for the same reason the row order is — and
 * because the never-cited tail is the finding, so it must reliably be the tail.
 *
 * The name tie-break goes through the reader's collator rather than a bare `localeCompare()`,
 * which sorts in the BROWSER's locale rather than the product's — so a Brazilian reading in
 * English got one order and their colleague another, from the same data. The collator also
 * folds accents and reads digits as numbers, which is what puts "Base 9" before "Base 10"
 * instead of after it (PRD-27 LOC-023).
 */
export const knowledgeRows = (fmt: Pick<I18n, 'compare'>, agent: UsageByAgent): UsageByAgent['knowledgeBases'] =>
  [...agent.knowledgeBases].sort(
    (a, b) => b.citations - a.citations || b.storageBytes - a.storageBytes || fmt.compare(a.name ?? '', b.name ?? '')
  )
